/**
 * Orthogonal edge routing for the Build canvas, ported from the Protolab
 * prototype (`prostudio-build-flow-layouts.html`: `segHitsRect`,
 * `pathHitsRects`, `cvRoute`, `cvMidpoint`, `cvPolyPath`). Every edge leaves
 * the side facing its target, runs along a corridor — a gap the layout already
 * leaves between cards — and enters on the side facing back, so a line goes
 * round a card rather than behind it.
 *
 * Pure geometry: no React, no store. Drawing only — which edges exist and what
 * kind they are is still `canvas-edges-model.ts`'s job.
 */

export type Point = [number, number];

export interface Rect {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface Corridors {
  x: number[];
  y: number[];
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/** Clearance a line keeps from a card. */
export const RECT_PAD = 12;
/** How far outside the layout the escape lanes sit. */
export const CORRIDOR = 26;

/** Segments here are always axis-aligned. */
const segHitsRect = (a: Point, b: Point, r: Rect): boolean => {
  const x1 = Math.min(a[0], b[0]);
  const x2 = Math.max(a[0], b[0]);
  const y1 = Math.min(a[1], b[1]);
  const y2 = Math.max(a[1], b[1]);
  return x1 < r.x2 && x2 > r.x1 && y1 < r.y2 && y2 > r.y1;
};

export const pathHitsRects = (pts: Point[], rects: Rect[]): boolean => {
  for (let i = 1; i < pts.length; i += 1) {
    for (let k = 0; k < rects.length; k += 1) {
      if (segHitsRect(pts[i - 1], pts[i], rects[k])) return true;
    }
  }
  return false;
};

export interface RouteElbowOptions {
  from: Point;
  to: Point;
  /** Cards the line must not cross — every card but its own two ends. */
  obstacles: Rect[];
  corridors: Corridors;
  prefer?: 'h' | 'v';
}

/**
 * An elbow with its long run on a corridor. Candidates are tried in order —
 * the midpoint first, then the layout's own gaps, then the lanes outside
 * everything; the first that clears every card wins. If none does, the
 * midpoint elbow is used so the edge at least stays orthogonal.
 */
export const routeElbow = ({
  from: p1,
  to: p2,
  obstacles: rects,
  corridors,
  prefer,
}: RouteElbowOptions): Point[] => {
  const [x1, y1] = p1;
  const [x2, y2] = p2;
  if (Math.abs(y1 - y2) < 1.5 || Math.abs(x1 - x2) < 1.5) {
    const straight: Point[] = [p1, p2];
    if (!pathHitsRects(straight, rects)) return straight;
  }

  const tries: Point[][] = [];
  if (prefer !== 'v') {
    [(x1 + x2) / 2, ...corridors.x, corridors.minX, corridors.maxX].forEach(
      (mx) =>
        tries.push([
          [x1, y1],
          [mx, y1],
          [mx, y2],
          [x2, y2],
        ])
    );
  }
  if (prefer !== 'h') {
    [(y1 + y2) / 2, ...corridors.y, corridors.minY, corridors.maxY].forEach(
      (my) =>
        tries.push([
          [x1, y1],
          [x1, my],
          [x2, my],
          [x2, y2],
        ])
    );
  }
  // Three bends let a line leave sideways, cross on a free lane and come
  // back, which is what gets past a card in the way.
  [...corridors.y, corridors.minY, corridors.maxY].forEach((my) => {
    [...corridors.x, corridors.minX, corridors.maxX].forEach((mx) => {
      tries.push([
        [x1, y1],
        [mx, y1],
        [mx, my],
        [x2, my],
        [x2, y2],
      ]);
    });
  });

  const clear = tries.find((pts) => !pathHitsRects(pts, rects));
  if (clear) return clear;
  const mx = (x1 + x2) / 2;
  return [
    [x1, y1],
    [mx, y1],
    [mx, y2],
    [x2, y2],
  ];
};

/**
 * Half way along a polyline by length, not by point count — a route with one
 * long leg and three short ones would otherwise put its label in a corner.
 */
export const polylineMidpoint = (pts: Point[]): Point => {
  if (pts.length === 0) return [0, 0];
  if (pts.length === 1) return pts[0];
  const seg: number[] = [];
  let total = 0;
  for (let i = 1; i < pts.length; i += 1) {
    const d = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    seg.push(d);
    total += d;
  }
  let want = total / 2;
  for (let i = 0; i < seg.length; i += 1) {
    if (want <= seg[i]) {
      const t = seg[i] ? want / seg[i] : 0;
      return [
        pts[i][0] + (pts[i + 1][0] - pts[i][0]) * t,
        pts[i][1] + (pts[i + 1][1] - pts[i][1]) * t,
      ];
    }
    want -= seg[i];
  }
  return pts[pts.length - 1];
};

/** A point list as an SVG path with rounded (R = 10) corners. */
export const roundedPolylinePath = (pts: Point[]): string => {
  const R = 10;
  if (pts.length === 0) return '';
  if (pts.length < 3) {
    const last = pts[pts.length - 1];
    return `M${pts[0][0]} ${pts[0][1]}L${last[0]} ${last[1]}`;
  }
  let d = `M${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length - 1; i += 1) {
    const [px, py] = pts[i - 1];
    const [cx, cy] = pts[i];
    const [nx, ny] = pts[i + 1];
    const inLen = Math.hypot(cx - px, cy - py);
    const outLen = Math.hypot(nx - cx, ny - cy);
    const r = Math.max(0, Math.min(R, inLen / 2, outLen / 2));
    if (r) {
      const ix = cx - ((cx - px) / (inLen || 1)) * r;
      const iy = cy - ((cy - py) / (inLen || 1)) * r;
      const ox = cx + ((nx - cx) / (outLen || 1)) * r;
      const oy = cy + ((ny - cy) / (outLen || 1)) * r;
      d += `L${ix} ${iy}Q${cx} ${cy} ${ox} ${oy}`;
    }
  }
  const last = pts[pts.length - 1];
  return `${d}L${last[0]} ${last[1]}`;
};

/**
 * Lanes between cards. The prototype records these while it lays cards out;
 * here the layout is dagre's, so they are read back off the result: merge the
 * padded card spans along one axis and take the middle of every gap between
 * them.
 */
const gapLanes = (spans: [number, number][]): number[] => {
  const sorted = [...spans].sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  sorted.forEach(([s, e]) => {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  });
  const lanes: number[] = [];
  for (let i = 1; i < merged.length; i += 1) {
    lanes.push((merged[i - 1][1] + merged[i][0]) / 2);
  }
  return lanes;
};

export const buildCorridors = (rects: Rect[]): Corridors => {
  if (rects.length === 0) {
    return { x: [], y: [], minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }
  return {
    x: gapLanes(rects.map((r) => [r.x1, r.x2])),
    y: gapLanes(rects.map((r) => [r.y1, r.y2])),
    minX: Math.min(...rects.map((r) => r.x1)) - CORRIDOR,
    maxX: Math.max(...rects.map((r) => r.x2)) + CORRIDOR,
    minY: Math.min(...rects.map((r) => r.y1)) - CORRIDOR,
    maxY: Math.max(...rects.map((r) => r.y2)) + CORRIDOR,
  };
};

export interface RoutableCard {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Row id -> the y (flow coordinates) of that row's centre, when open. */
  rowY?: Map<string, number>;
}

export interface RoutableEdge {
  id: string;
  source: string;
  target: string;
  sourceRowId?: string;
  targetRowId?: string;
}

export interface RoutedWire {
  id: string;
  pts: Point[];
  /** Where the line ends — the target dot. */
  end: Point;
  /** Polyline midpoint by length. */
  mid: Point;
  /**
   * Where the label pill sits, and which way it hangs off that point. A line
   * whose last leg is long enough carries its label on that leg, just short
   * of the card it reaches (`before`/`after` the point, on the line), so the
   * name reads next to its agent. Otherwise the label is centred on `mid`.
   */
  label: Point;
  labelAlign: 'center' | 'before' | 'after';
}

type Side = 'l' | 'r';

/**
 * Where a line meets a card: the row of the node that actually routes, when
 * the card is open and that row is known; otherwise the middle of the side.
 */
const port = (card: RoutableCard, side: Side, rowId?: string): Point => {
  const rowY = rowId ? card.rowY?.get(rowId) : undefined;
  const y = rowY ?? card.y + card.h / 2;
  return side === 'r' ? [card.x + card.w, y] : [card.x, y];
};

/** The first fan lane's distance from the port it leaves. */
export const FAN_START = 18;
/** The gap between two neighbouring fan lanes. */
export const FAN_GAP = 6;
/** A last leg shorter than this keeps its label at the midpoint. */
const LABEL_LEG_MIN = 48;
/** Room between a hung label and the card it reaches. */
const LABEL_INSET = 10;

/**
 * Where the label goes. On the last leg when it is horizontal and long
 * enough, hung just before the target; otherwise the midpoint.
 */
const placeLabel = (
  pts: Point[],
  mid: Point
): Pick<RoutedWire, 'label' | 'labelAlign'> => {
  if (pts.length >= 2) {
    const [ax, ay] = pts[pts.length - 2];
    const [bx, by] = pts[pts.length - 1];
    if (Math.abs(ay - by) < 0.5 && Math.abs(bx - ax) >= LABEL_LEG_MIN) {
      return bx > ax
        ? { label: [bx - LABEL_INSET, by], labelAlign: 'before' }
        : { label: [bx + LABEL_INSET, by], labelAlign: 'after' };
    }
  }
  return { label: mid, labelAlign: 'center' };
};

interface Planned {
  edge: RoutableEdge;
  a: RoutableCard;
  b: RoutableCard;
  fromSide: Side;
  p1: Point;
  p2: Point;
}

/**
 * Several lines leaving the same port (every Supervisor intent leaves its
 * routing row) used to share one trunk, drawn on top of each other, so you
 * could not follow any one of them. Each now gets its own vertical lane,
 * stepping out from the port: the line to the farthest card turns first,
 * the one to the nearest card last. Ordered that way, no line's last leg
 * crosses another's trunk. Lines going up and lines going down are ordered
 * separately. A line a card blocks takes the innermost lane and is routed
 * on from there.
 */
const fanLanes = (
  plans: Planned[],
  obstaclesFor: (plan: Planned) => Rect[],
  corridors: Corridors
): Map<RoutableEdge, Point[]> => {
  const out = new Map<RoutableEdge, Point[]>();
  const portOf = (plan: Planned) =>
    `${plan.a.id}|${plan.fromSide}|${Math.round(plan.p1[1])}`;
  const perPort = new Map<string, number>();
  plans.forEach((plan) =>
    perPort.set(portOf(plan), (perPort.get(portOf(plan)) ?? 0) + 1)
  );
  const groups = new Map<string, Planned[]>();
  plans.forEach((plan) => {
    // A port with one line has nothing to keep apart; a level line is drawn
    // straight.
    if ((perPort.get(portOf(plan)) ?? 0) < 2) return;
    if (Math.abs(plan.p1[1] - plan.p2[1]) < 1.5) return;
    const up = plan.p2[1] < plan.p1[1];
    const key = `${portOf(plan)}|${up}`;
    groups.set(key, [...(groups.get(key) ?? []), plan]);
  });
  groups.forEach((group) => {
    const dir = group[0].fromSide === 'r' ? 1 : -1;
    const laneAt = (i: number) =>
      group[0].p1[0] + dir * (FAN_START + i * FAN_GAP);
    const straight = (plan: Planned, lane: number): Point[] => [
      plan.p1,
      [lane, plan.p1[1]],
      [lane, plan.p2[1]],
      plan.p2,
    ];
    // A line a card blocks (its target sits beyond the next column) cannot run
    // straight up its lane; it takes the innermost lanes, nearest the port,
    // and the router carries it on from there. Inside every other trunk, it
    // crosses none of their last legs.
    const blocked = group.filter((plan) =>
      pathHitsRects(straight(plan, laneAt(0)), obstaclesFor(plan))
    );
    const clear = group
      .filter((plan) => !blocked.includes(plan))
      .sort(
        (x, y) => Math.abs(y.p2[1] - y.p1[1]) - Math.abs(x.p2[1] - x.p1[1])
      );
    blocked.forEach((plan, i) => {
      const lane = laneAt(i);
      const start: Point = [lane, plan.p1[1]];
      const rest = routeElbow({
        from: start,
        to: plan.p2,
        obstacles: obstaclesFor(plan),
        corridors,
        prefer: 'v',
      });
      out.set(plan.edge, [plan.p1, ...rest]);
    });
    clear.forEach((plan, i) => {
      const pts = straight(plan, laneAt(blocked.length + i));
      if (!pathHitsRects(pts, obstaclesFor(plan))) out.set(plan.edge, pts);
    });
  });
  return out;
};

export const routeWires = (
  cards: RoutableCard[],
  edges: RoutableEdge[]
): RoutedWire[] => {
  const byId = new Map(cards.map((c) => [c.id, c]));
  const rects: Rect[] = cards.map((c) => ({
    id: c.id,
    x1: c.x - RECT_PAD,
    y1: c.y - RECT_PAD,
    x2: c.x + c.w + RECT_PAD,
    y2: c.y + c.h + RECT_PAD,
  }));
  const corridors = buildCorridors(rects);

  const plans: Planned[] = [];
  edges.forEach((edge) => {
    const a = byId.get(edge.source);
    const b = byId.get(edge.target);
    if (!a || !b || a === b) return;

    // Face whichever way actually points at the other card.
    const fromSide: Side = b.x + b.w / 2 >= a.x + a.w / 2 ? 'r' : 'l';
    const toSide: Side = fromSide === 'r' ? 'l' : 'r';
    plans.push({
      edge,
      a,
      b,
      fromSide,
      p1: port(a, fromSide, edge.sourceRowId),
      p2: port(b, toSide, edge.targetRowId),
    });
  });

  const obstaclesFor = (plan: Planned) =>
    rects.filter((r) => r.id !== plan.a.id && r.id !== plan.b.id);
  const fanned = fanLanes(plans, obstaclesFor, corridors);

  return plans.map((plan) => {
    const pts =
      fanned.get(plan.edge) ??
      routeElbow({
        from: plan.p1,
        to: plan.p2,
        obstacles: obstaclesFor(plan),
        corridors,
        prefer: 'h',
      });
    const mid = polylineMidpoint(pts);
    return {
      id: plan.edge.id,
      pts,
      end: plan.p2,
      mid,
      ...placeLabel(pts, mid),
    };
  });
};
